import pRetry from "p-retry";
import qs from "qs";
import { Mapper } from "../../../../lib/utils/mapper";
import { getBonusxCmsConfig } from "../../../common/bonusxCmsConfig";
import type { IApiClientService } from "../../ApiClient/ApiClientService.interface";
import AxiosApiClientInstance from "../../AxiosApiClientInstance/AxiosApiClientInstance";
import type { IApiClientResponse } from "../../AxiosApiClientInstance/dtos/AxiosApiClientResponse";
import type { ICmsSubscriptionResponseDto } from "../dtos/cms-subscription-remote/cms-subscription-remote.dto.interface";
import type { ICmsSubscriptionWithPricingResponseDto } from "../dtos/cms-subscription-with-pricing-remote/cms-subscription-with-pricing-remote.dto.interface";
import { CmsSubscriptionWithPricingDto } from "../dtos/cms-subscription-with-pricing/cms-subscription-with-pricing.dto";
import type { ICmsSubscriptionWithPricingDto } from "../dtos/cms-subscription-with-pricing/cms-subscription-with-pricing.dto.interface";
import { CmsSubscriptionDto } from "../dtos/cms-subscription/cms-subscription.dto";
import type { ICmsSubscriptionDto } from "../dtos/cms-subscription/cms-subscription.dto.interface";
import type { ICmsSubscriptionRemoteDatasource } from "./cms-subscription-remote.datasource.interface";

export class CmsSubscriptionRemoteDatasource implements ICmsSubscriptionRemoteDatasource {
  private readonly _locale = "it";
  private readonly _PAGE_SIZE = 1000;
  private readonly _SORT_BY_ORDER_ASCENDING = "order:asc";
  private readonly _DEFAULT_QUERY = {
    pagination: { pageSize: this._PAGE_SIZE },
    locale: this._locale,
    sort: this._SORT_BY_ORDER_ASCENDING,
  };
  private readonly _BASE_URL = "api/subscriptions";
  private readonly _MAX_RETRY_ATTEMPTS = 3;

  constructor(private readonly _client: IApiClientService = new AxiosApiClientInstance(getBonusxCmsConfig())) {}

  public async getOrderedSubscriptions(): Promise<ICmsSubscriptionDto[]> {
    const query = qs.stringify(this._DEFAULT_QUERY);
    const response = await this._performGetOperationWithRetry<ICmsSubscriptionResponseDto>(
      this.getOrderedSubscriptions.name,
      query
    );

    const data = response.data.data;

    const dtos = data.map(function mapToDto({ attributes }) {
      const subscriptionDto = Mapper.mapData(CmsSubscriptionDto, attributes);
      return subscriptionDto;
    });

    return dtos;
  }

  public async getSubscriptionsWithPricing(): Promise<ICmsSubscriptionWithPricingDto[]> {
    const subscriptionsWithPricingQuery = {
      ...this._DEFAULT_QUERY,
      populate: {
        subscription_frequencies: "*",
      },
    };
    const query = qs.stringify(subscriptionsWithPricingQuery);
    const response = await this._performGetOperationWithRetry<ICmsSubscriptionWithPricingResponseDto>(
      this.getSubscriptionsWithPricing.name,
      query
    );
    const data = response.data.data;

    const dtos = data.map(function mapToDto({ attributes }) {
      const subscriptionDto = Mapper.mapData(CmsSubscriptionWithPricingDto, attributes);
      return subscriptionDto;
    });

    return dtos;
  }

  private async _performGetOperationWithRetry<T>(callerName: string, query: string): Promise<IApiClientResponse<T>> {
    const decodedURL = decodeURIComponent(`${this._BASE_URL}?${query}`);
    const fetch = () => this._client.get<T>(decodedURL);

    const response = await pRetry(fetch, {
      onFailedAttempt: (error) => {
        console.warn(
          `${callerName} ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`,
          error.message
        );
      },
      retries: this._MAX_RETRY_ATTEMPTS,
    });

    return response;
  }
}
